import { DownOutlined } from "@ant-design/icons";
import { Select, type SelectProps, Space, Typography } from "antd";
import { useAutoSelectSingleOption } from "hooks/useAutoSelectSingleOption";
import { ReactNode, useCallback, useMemo } from "react";

export type PillSelectOption<T extends string> = {
  description?: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  searchText?: string;
  value: T;
};

type Props<T extends string> = {
  disabled?: boolean;
  onChange: (value: T) => void;
  options: PillSelectOption<T>[];
  placeholder?: ReactNode;
  searchable?: boolean;
  value: T | undefined;
};

const optionSearchText = <T extends string>(opt: PillSelectOption<T>): string =>
  opt.searchText ?? (typeof opt.label === "string" ? opt.label : opt.value);

const SUFFIX_ICON = <DownOutlined className="text-[10px]" />;

export const PillSelect = <T extends string>({
  disabled,
  onChange,
  options,
  placeholder,
  searchable,
  value,
}: Props<T>) => {
  const { locked } = useAutoSelectSingleOption(options, value, onChange);
  const autoLocked = locked && !disabled;
  const effectiveDisabled = disabled || locked;

  const descriptionsByValue = useMemo(
    () => new Map(options.map((o) => [o.value, o.description])),
    [options]
  );

  const renderOption = useCallback<NonNullable<SelectProps<T>["optionRender"]>>(
    (option) => {
      const description = descriptionsByValue.get(option.value as T);
      return description ? (
        <div>
          <div>{option.label}</div>
          <Typography.Text type="secondary" className="text-xs">
            {description}
          </Typography.Text>
        </div>
      ) : (
        option.label
      );
    },
    [descriptionsByValue]
  );

  const selectOptions = useMemo(
    () =>
      options.map((opt) => ({
        disabled: opt.disabled,
        label: opt.icon ? (
          <Space size={6}>
            {opt.icon}
            <span>{opt.label}</span>
          </Space>
        ) : (
          opt.label
        ),
        searchText: optionSearchText(opt),
        value: opt.value,
      })),
    [options]
  );

  return (
    <span className="[&_.ant-select]:min-w-32">
      <Select<T>
        className={autoLocked ? "auto-locked-select" : undefined}
        disabled={effectiveDisabled}
        onChange={onChange}
        optionFilterProp="searchText"
        optionRender={renderOption}
        options={selectOptions}
        placeholder={placeholder ?? "Select…"}
        popupMatchSelectWidth={false}
        showSearch={searchable}
        suffixIcon={SUFFIX_ICON}
        value={value}
      />
    </span>
  );
};
